import React, { useState, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Building2, ClipboardCheck, AlertTriangle, LayoutDashboard,
  LogOut, Users, Menu, X, Bell, CalendarClock, FileText,
  Settings, Mail, Inbox,
} from 'lucide-react';
import { motion, AnimatePresence } from "framer-motion";
import { auth } from "./apiClient";
import { useCurrentUser, canSeeCorrespondence, canSeeConstructionControl, canSeeDocumentation } from "./hooks/useCurrentUser";
import { useOnClickOutside } from "./hooks/useOnClickOutside";
import { ScrollProvider } from "./contexts/ScrollContext";

// ─── Только эти группы можно скрывать ───────────────────────────────────────
const NAV_GROUPS = [
  {
    label: "Обзор",
    items: [
      { title: "Дашборд", url: "/", icon: LayoutDashboard },
      { title: "События", url: "/events", icon: Bell },
      { title: "Объекты", url: "/projects", icon: Building2 },
    ],
  },
  {
    label: "Строительный контроль",
    items: [
      { title: "Заявки на выезд", url: "/requests", icon: CalendarClock },
      { title: "Проверки", url: "/inspections", icon: ClipboardCheck },
      { title: "Недостатки", url: "/defects", icon: AlertTriangle },
    ],
  },
  {
    label: "Ведение документации",
    items: [
      { title: "Проектная док.", url: "/documentation", icon: FileText },
      { title: "Реестр замечаний", url: "/documentation/remarks", icon: AlertTriangle },
    ],
  },
  {
    label: "Документооборот",
    items: [
      { title: "Корреспонденция", url: "/correspondence", icon: Mail },
    ],
  },
];


const ADMIN_GROUP = {
  label: "Администрирование",
  items: [
    { title: "Пользователи", url: "/users", icon: Users },
  ],
};

// ─── Ключ localStorage ───────────────────────────────────────────────────────
const LS_KEY = 'stroycontrol_hidden_categories';

function loadHidden() {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; }
  catch { return []; }
}

// ─── NavItem ─────────────────────────────────────────────────────────────────
function NavItem({ item, isActive, onClick }) {
  return (
    <Link
      to={item.url}
      onClick={onClick}
      className={[
        "relative flex items-center gap-3 px-3 py-2.5 rounded text-sm transition-all duration-150 group",
        isActive
          ? "sidebar-text-active font-medium nav-active"
          : "sidebar-text hover:text-white/90 hover:bg-white/5",
      ].join(" ")}
    >
      <item.icon
        className={[
          "w-4 h-4 flex-shrink-0 transition-colors",
          isActive ? "text-brand-400" : "text-current opacity-60 group-hover:opacity-100",
        ].join(" ")}
      />
      <span className="flex-1 truncate">{item.title}</span>
      {isActive && (
        <span className="w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0" />
      )}
    </Link>
  );
}

// ─── Layout ──────────────────────────────────────────────────────────────────
export default function Layout({ children }) {
  const location = useLocation();
  const { user } = useCurrentUser();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [hiddenCategories, setHiddenCategories] = useState(loadHidden);
  const scrollContainerRef = useRef(null);
  const settingsRef = useRef(null);

  const isAdmin = user?.role === "admin";
  const isInspector = user?.role_type === "inspector";

  // Закрыть настройки при клике вне блока
  useOnClickOutside(settingsRef, useCallback(() => setShowSettings(false), []));

  // Переключить видимость категории
  const toggleCategory = (label) => {
    const next = hiddenCategories.includes(label)
      ? hiddenCategories.filter(c => c !== label)
      : [...hiddenCategories, label];
    setHiddenCategories(next);
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  };

  // Собираем финальный список групп с учётом прав
  const visibleGroups = NAV_GROUPS.filter(g => {
    if (hiddenCategories.includes(g.label)) return false;
    if (isAdmin || isInspector) return true;
    if (g.label === "Строительный контроль") return canSeeConstructionControl(user);
    if (g.label === "Ведение документации") return canSeeDocumentation(user);
    if (g.label === "Документооборот") return canSeeCorrespondence(user);
    return true;
  });
  if (isAdmin) visibleGroups.push(ADMIN_GROUP);

  const closeMobile = () => setIsMobileMenuOpen(false);

  const initials = user?.full_name
    ? user.full_name.split(' ').map(w => w[0]).slice(0, 2).join('')
    : '?';

  const roleLabel = user?.role === 'admin'
    ? 'Администратор'
    : user?.role_type === 'inspector'
    ? 'Инспектор'
    : 'Пользователь';

  return (
    <div className="min-h-screen flex w-full bg-ink-50 font-sans">

      {/* Mobile overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-ink-950/60 backdrop-blur-[2px] z-40 md:hidden"
            onClick={closeMobile}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={[
          "sidebar-bg fixed inset-y-0 left-0 z-50 flex flex-col",
          "transition-transform duration-300 ease-out",
          "md:static md:translate-x-0",
          "border-r sidebar-border",
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full",
        ].join(" ")}
        style={{ width: 'var(--sidebar-width, 260px)' }}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-5 py-4 border-b sidebar-border h-[60px]">
          <Link to="/" className="flex items-center gap-2.5" onClick={closeMobile}>
            <div className="relative w-7 h-7 flex-shrink-0">
              <div className="absolute inset-0 border-2 border-brand-500 rounded-sm" />
              <div className="absolute inset-[4px] bg-brand-500 rounded-[1px]" />
            </div>
            <div>
              <span className="text-white font-bold text-sm tracking-tight">
                СтройКонтроль
              </span>
              <div className="text-[10px] sidebar-text tracking-widest uppercase font-mono">
                ООО «ТДО»
              </div>
            </div>
          </Link>

          {/* Mobile close */}
          <button
            onClick={closeMobile}
            className="md:hidden p-1.5 rounded sidebar-text hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto sidebar-scroll py-3 px-3 space-y-4">
          {visibleGroups.map((group) => (
            <div key={group.label}>
              <p className="px-3 mb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white/25 font-mono">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavItem
                    key={item.url}
                    item={item}
                    isActive={location.pathname === item.url}
                    onClick={closeMobile}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User block */}
        <div className="border-t sidebar-border p-3 relative" ref={settingsRef}>

          {/* ── Всплывающий блок настроек ── */}
          <AnimatePresence>
            {showSettings && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                transition={{ duration: 0.15 }}
                className="absolute bottom-full left-3 right-3 mb-2 rounded-lg border sidebar-border overflow-hidden"
                style={{ background: 'var(--sidebar-settings-bg, #1e2433)' }}
              >
                {/* Заголовок */}
                <div className="px-3 py-2 border-b sidebar-border flex items-center gap-2">
                  <Settings className="w-3.5 h-3.5 text-white/40" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 font-mono">
                    Разделы меню
                  </span>
                </div>

                {/* Список групп (только видимые для роли) */}
                <div className="py-1">
                  {NAV_GROUPS
                    .filter(g => {
                      if (isAdmin || isInspector) return true;
                      if (g.label === "Строительный контроль") return canSeeConstructionControl(user);
                      if (g.label === "Ведение документации") return canSeeDocumentation(user);
                      if (g.label === "Документооборот") return canSeeCorrespondence(user);
                      return true;
                    })
                    .map(group => {
                      const hidden = hiddenCategories.includes(group.label);
                      return (
                        <button
                          key={group.label}
                          onClick={() => toggleCategory(group.label)}
                          className="flex items-center gap-3 w-full px-3 py-2 text-sm sidebar-text hover:bg-white/5 transition-colors"
                        >
                          {/* Чекбокс */}
                          <span className={[
                            "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors",
                            hidden
                              ? "border-white/20 bg-transparent"
                              : "border-brand-500 bg-brand-500/20",
                          ].join(" ")}>
                            {!hidden && (
                              <span className="w-2 h-2 rounded-sm bg-brand-400 block" />
                            )}
                          </span>
                          <span className={hidden ? "opacity-40" : "opacity-90"}>
                            {group.label}
                          </span>
                        </button>
                      );
                    })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Аватарка + имя (кликабельно) */}
          {user && (
            <button
              onClick={() => setShowSettings(v => !v)}
              className="flex items-center gap-3 w-full px-2 py-2 mb-1 rounded hover:bg-white/5 transition-colors text-left group"
            >
              {/* Avatar */}
              <div className={[
                "w-8 h-8 rounded flex items-center justify-center flex-shrink-0 transition-colors",
                showSettings
                  ? "bg-brand-500/30 border border-brand-400/50"
                  : "bg-brand-600/20 border border-brand-500/30",
              ].join(" ")}>
                <span className="text-xs font-bold text-brand-300 font-mono">
                  {initials}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white/90 truncate leading-tight">
                  {user.full_name}
                </div>
                <div className="text-[10px] sidebar-text font-mono truncate">
                  {roleLabel}
                </div>
              </div>
              {/* Индикатор */}
              <Settings className={[
                "w-3.5 h-3.5 flex-shrink-0 transition-all duration-150",
                showSettings ? "text-brand-400 opacity-100 rotate-45" : "text-white/0 group-hover:text-white/30 group-hover:opacity-100",
              ].join(" ")} />
            </button>
          )}

          <button
            onClick={auth.logout}
            className="flex items-center gap-2.5 w-full px-3 py-2 rounded text-sm sidebar-text hover:text-rose-400 hover:bg-rose-500/10 transition-all duration-150"
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            <span>Выйти</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Mobile topbar */}
        <header className="bg-white border-b border-ink-200 px-4 h-[60px] md:hidden flex items-center gap-3 shrink-0">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="p-2 -ml-1.5 text-ink-600 hover:bg-ink-100 rounded transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="relative w-6 h-6 flex-shrink-0">
              <div className="absolute inset-0 border-2 border-brand-500 rounded-sm" />
              <div className="absolute inset-[3px] bg-brand-500 rounded-[1px]" />
            </div>
            <span className="text-sm font-bold text-ink-900">СтройКонтроль</span>
          </div>
        </header>

        {/* Scroll container */}
        <ScrollProvider value={scrollContainerRef}>
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-auto scroll-smooth"
          >
            {children}
          </div>
        </ScrollProvider>
      </main>
    </div>
  );
}
